import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { AlertTriangle, ExternalLink, MapPin } from 'lucide-react';
import { useDocsOrgSiteSummary, type FaAsset, type FaField } from '@/api/docs';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

/**
 * IT Glue "Site Summary" (server/docs/siteSummary.ts) at the top of the Docs org page: one compact
 * card per record (some clients have one per office) with the relationship status as a badge,
 * known issues called out first, then the people fields side by side and long text full width.
 * Renders nothing when the org has no Site Summary. Passwords are never included (server side).
 */

const SANITIZE: Parameters<typeof DOMPurify.sanitize>[1] = { ADD_ATTR: ['target'], FORBID_TAGS: ['style', 'form', 'input', 'button'] };
const HTML_CLS = 'rounded-md bg-white px-3 py-2 text-sm leading-relaxed text-neutral-900 [&_a]:text-blue-700 [&_a]:underline [&_li]:ml-5 [&_ol]:list-decimal [&_p]:my-1 [&_ul]:list-disc';

/** Relationship status → tone. Unknown values stay neutral. */
function statusTone(s: string) {
  const v = s.toLowerCase();
  if (/happy|good|great|healthy/.test(v)) return 'bg-success-soft text-success';
  if (/unhappy|at risk|risk|poor|churn|bad/.test(v)) return 'bg-destructive/10 text-destructive';
  if (/neutral|ok|concern|watch/.test(v)) return 'bg-warning-soft text-warning';
  return 'bg-muted text-muted-foreground';
}

export function SiteSummaryPanel({ orgId }: { orgId: string }) {
  const { data } = useDocsOrgSiteSummary(orgId);
  const items = data?.items ?? [];
  if (!items.length) return null;
  return (
    <div className={cn('mt-3 grid gap-3', items.length > 1 && 'lg:grid-cols-2')}>
      {items.map((s) => <SummaryCard key={s.id} s={s} />)}
    </div>
  );
}

function SummaryCard({ s }: { s: FaAsset }) {
  const known = s.fields.find((f) => f.kind === 'html' && /known issues/i.test(f.label));
  const rest = s.fields.filter((f) => f !== known);
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-divider px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h2 className="truncate text-sm font-semibold text-foreground">{s.name}</h2>
          {s.badge && <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold', statusTone(s.badge))}>{s.badge}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-3 text-[11px] text-muted-foreground">
          {s.updatedAt && <span>Updated {fmtRelative(s.updatedAt)}</span>}
          {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground hover:underline">IT Glue <ExternalLink className="h-3 w-3" /></a>}
        </div>
      </div>
      <div className="px-4 py-3">
        {known && (
          <div className="mb-3 rounded-md border border-warning/30 bg-warning-soft px-3 py-2">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-warning"><AlertTriangle className="h-3.5 w-3.5" />{known.label}</div>
            <Html value={known.value ?? ''} />
          </div>
        )}
        <Fields fields={rest} />
      </div>
    </section>
  );
}

/** Headers become small section labels; short fields sit in a two-column grid; HTML spans full width. */
function Fields({ fields }: { fields: FaField[] }) {
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
      {fields.map((f, i) => {
        if (f.kind === 'header') {
          return <h3 key={`${i}:${f.label}`} className="col-span-full pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:pt-0">{f.label}</h3>;
        }
        if (f.kind === 'html') {
          return (
            <div key={`${i}:${f.label}`} className="col-span-full">
              <div className="mb-0.5 text-xs text-muted-foreground">{f.label}</div>
              <Html value={f.value ?? ''} />
            </div>
          );
        }
        return (
          <div key={`${i}:${f.label}`} className="min-w-0">
            <div className="text-xs text-muted-foreground">{f.label}</div>
            <div className="text-foreground">
              {f.kind === 'tags'
                ? f.tags!.map((t, j) => (
                  <span key={`${t.name}${j}`}>
                    {j > 0 && ', '}
                    {t.url ? <a href={t.url} target="_blank" rel="noreferrer" className="hover:underline">{t.name}</a> : t.name}
                  </span>
                ))
                : <span className="whitespace-pre-line">{f.value}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Html({ value }: { value: string }) {
  const html = useMemo(() => (DOMPurify.sanitize(value, SANITIZE) as unknown as string).replace(/<a /g, '<a target="_blank" rel="noreferrer" '), [value]);
  return <div className={HTML_CLS} dangerouslySetInnerHTML={{ __html: html }} />;
}
